import { ahuBoardApi } from 'app/services/ahuBoardApi';

/**
 * COMBO LOUVER (KA-5245) — sub-phase A1.2 data layer.
 *
 * Two read-only GETs the combo dialog needs and nothing else does:
 *
 *  R5/R6 — `GET /api/v1.0/cms/<mfg>/v1.0.0/ahu/louvers/aw_dims/?…&front=true`
 *          and the `&side=true` twin (`_fetchComboAwDims` :1354-1411). Issued as
 *          TWO PARALLEL calls off one base URL because the enclosure constrains
 *          the front and the sides differently: the front width is bounded by
 *          `combo_louver_enclosure_width_max` (or, with `auto_size=false`, by the
 *          request's own `&width=`), each side width by the box DEPTH
 *          (`louvers_viewset.py:686-730`). Left/right share ONE fetch — both
 *          sides are symmetric in the enclosure.
 *          Response is `{aw_widths:[…], aw_heights:[…]}` — FLAT, independently
 *          sorted number arrays; the pairs are NOT correlated, so a (w, h) combo
 *          taken from them may not exist as a catalog part. That is what the
 *          per-face `/louvers/all/` lookup below settles.
 *
 *  R7 —    `GET /api/v1.0/cms/<mfg>/v1.0.0/ahu/louvers/all/?…&aw_width&aw_height`
 *          (`_recalcComboMasterLouverId` :1735-1780). `results[0].id` becomes
 *          THAT FACE's `master_louver_id`; an empty list means "this size has no
 *          matching catalog louver" → the face is flagged `.not-valid`.
 *
 * ⚠ CACHING, deliberately asymmetric — it mirrors legacy exactly:
 *  · aw_dims is NOT cached (`keepUnusedDataFor: 0`, callers pass
 *    `forceRefetch: true`). Legacy calls `fn.uni_tools.get` with no
 *    `stateStorage` memo, and the SAME url is legitimately re-requested over and
 *    over (QUIRK Q10 — one enclosure edit yields 2-3 (aw_dims, with_position)
 *    pairs). A sticky cache would also make the Q5/R1 500 permanent.
 *  · the per-face louver lookup IS cached, because legacy memoizes it in
 *    `stateStorage.models.get('combo_master_louver', url, true)` (:1749-1752) so
 *    identical mfg/series/frame/blade + W×H across faces (both sides usually
 *    match) share ONE in-flight/cached request. RTKQ's URL cache + native
 *    in-flight dedup is the identical semantic.
 *
 * ⚠ RISK R1 (report-only, NEVER defend against): `louvers_viewset.py:692-715`
 * parses `width` / `height` / `depth` with a bare `float()`, and legacy builds
 * the aw_dims query by STRING CONCATENATION, so an unset dimension arrives as the
 * literal `"null"` and the endpoint 500s. Reproduced 6× after Reset. The port
 * must send the same `"null"` and let the failure through — see
 * `buildComboAwDimsUrl` and the engine's `fetchAwDims`.
 */

/** One `aw_dims` response — flat, independently sorted, uncorrelated (R5). */
export interface ComboAwDimsResponse {
  aw_widths: number[];
  aw_heights: number[];
}

export type ComboMasterLouverRow = { id: number } & Record<string, unknown>;

export const comboLouverApi = ahuBoardApi.injectEndpoints({
  endpoints: (builder) => ({
    /** R5/R6 — `/louvers/aw_dims/?…&front=true` | `&side=true` (raw URL). */
    getComboAwDims: builder.query<ComboAwDimsResponse, string>({
      query: (url) => url,
      transformResponse: (raw: unknown) => {
        const bag = raw as { aw_widths?: unknown; aw_heights?: unknown } | undefined;
        return {
          aw_widths: Array.isArray(bag?.aw_widths) ? (bag!.aw_widths as number[]) : [],
          aw_heights: Array.isArray(bag?.aw_heights) ? (bag!.aw_heights as number[]) : [],
        };
      },
      // legacy never memoizes this one — see the caching note above.
      keepUnusedDataFor: 0,
    }),

    /** R7 — `/louvers/all/?…&aw_width&aw_height`; `results[0]` is the match. */
    getComboMasterLouvers: builder.query<ComboMasterLouverRow[], string>({
      query: (url) => url,
      transformResponse: (raw: unknown) =>
        Array.isArray(raw)
          ? (raw as ComboMasterLouverRow[])
          : (((raw as { results?: unknown })?.results as ComboMasterLouverRow[]) ?? []),
      // legacy memoizes via stateStorage.models('combo_master_louver', url).
      keepUnusedDataFor: 24 * 60 * 60,
    }),
  }),
  overrideExisting: false,
});

export const { useLazyGetComboAwDimsQuery, useLazyGetComboMasterLouversQuery } = comboLouverApi;
